import _ from 'lodash';
import pricingHelper from '$$helpers/pricingHelper';
import routerHelper from '$$routerHelper';
import constants from '$$helpers/constants';
import userCustomColumns from '../../../controllers/userCustomColumns';

const customColumnsFeatureKey = 'custom_fields';
const alignForColumns = {
  [constants.CUSTOM_COLUMNS_TYPES.select.id]: 'left',
  [constants.CUSTOM_COLUMNS_TYPES.text.id]: 'left',
  [constants.CUSTOM_COLUMNS_TYPES.number.id]: 'right',
  [constants.CUSTOM_COLUMNS_TYPES.date.id]: 'left',
  [constants.CUSTOM_COLUMNS_TYPES.checkbox.id]: 'left',
  [constants.CUSTOM_COLUMNS_TYPES.color.id]: 'left',
  [constants.CUSTOM_COLUMNS_TYPES.multiselect.id]: 'left',
  [constants.CUSTOM_COLUMNS_TYPES.resources.id]: 'left',
  [constants.CUSTOM_COLUMNS_TYPES.tags.id]: 'left',
};
const editorsForColumns = {
  [constants.CUSTOM_COLUMNS_TYPES.select.id]: 'searchselect',
  [constants.CUSTOM_COLUMNS_TYPES.text.id]: 'text',
  [constants.CUSTOM_COLUMNS_TYPES.number.id]: 'custom_counter',
  [constants.CUSTOM_COLUMNS_TYPES.date.id]: 'custom_datepicker',
  [constants.CUSTOM_COLUMNS_TYPES.checkbox.id]: 'checkbox',
  [constants.CUSTOM_COLUMNS_TYPES.color.id]: 'select_color',
  [constants.CUSTOM_COLUMNS_TYPES.multiselect.id]: 'searchmultiselect',
  [constants.CUSTOM_COLUMNS_TYPES.resources.id]: 'resources',
  [constants.CUSTOM_COLUMNS_TYPES.tags.id]: 'tags',
};
const dataTypesForColumns = {
  [constants.CUSTOM_COLUMNS_TYPES.select.id]: 'custom_select',
  [constants.CUSTOM_COLUMNS_TYPES.text.id]: 'custom_text',
  [constants.CUSTOM_COLUMNS_TYPES.number.id]: 'custom_number',
  [constants.CUSTOM_COLUMNS_TYPES.date.id]: 'custom_date',
  [constants.CUSTOM_COLUMNS_TYPES.checkbox.id]: 'custom_checkbox',
  [constants.CUSTOM_COLUMNS_TYPES.color.id]: 'custom_color',
  [constants.CUSTOM_COLUMNS_TYPES.multiselect.id]: 'custom_multiselect',
  [constants.CUSTOM_COLUMNS_TYPES.resources.id]: 'custom_resources',
  [constants.CUSTOM_COLUMNS_TYPES.tags.id]: 'custom_tags',
};

class UserColumns {
  #instance;

  init(instance) {
    this.#instance = instance;

    const isFeatureAvailable = pricingHelper.checkPricingAccess(customColumnsFeatureKey);

    if (routerHelper.isListViewRoute() || routerHelper.isWorkloadViewRoute() || !isFeatureAvailable) {
      return;
    }

    this.#setColumns();
  }

  #setColumns() {
    this.#getColumnsData()
      .forEach(column => {
        const taskTypesForColumn = column.type !== constants.CUSTOM_COLUMNS_TYPES.number.id
          ? ['task', 'milestone', 'project']
          : ['task', 'milestone'];

        this.#instance.config.columns.push({
          id: column.id,
          align: alignForColumns[column.type],
          label: column.name,
          name: `${column.id}`, // must be string (for columns reorder)
          ganttIds: column.ganttIds || null,
          resize: true,
          hide: true,
          isShowCheckbox: true,
          isUserCustomColumn: true,
          columnType: column.type,
          control_type: editorsForColumns[column.type],
          control_options: this.#getOptionsForSelectInlineEditor(column),
          control_id: column.id,
          task_types: taskTypesForColumn,
          data_type: dataTypesForColumns[column.type],
          template: task => userCustomColumns.getValueForUserColumn(task, column).text,
        });
      });
  }

  #getColumnsData() {
    const viewData = this.#instance.ganttDataApi.getActiveProjectData();

    if (!viewData) {
      return [];
    }

    if (viewData.gantt_id) {
      const projectColumns = this.#instance.ganttDataApi.getProjectColumnsDataByProjectId(viewData.gantt_id);
      const columnIds = projectColumns ? projectColumns.columns : [];

      return columnIds.reduce((result, columnId) => {
        const column = this.#instance.ganttDataApi.getCustomColumnById(columnId);

        if (column) {
          result.push(column);
        }

        return result;
      }, []);
    }

    if (viewData.ganttIDs) {
      return _.reduce(viewData.ganttIDs, (columns, ganttID) => {
        const projectColumns = this.#instance.ganttDataApi.getProjectColumnsDataByProjectId(ganttID);
        const columnIds = projectColumns ? projectColumns.columns : [];

        columnIds.forEach(columnId => {
          const column = this.#instance.ganttDataApi.getCustomColumnById(columnId);

          if (column) {
            const addedColumn = columns.find(item => item.id === column.id);

            if (!addedColumn) {
              column.ganttIds = [ganttID];
              columns.push(column);
            } else {
              addedColumn.ganttIds.push(ganttID);
            }
          }
        });

        return columns;
      }, []);
    }
  }

  #getOptionsForSelectInlineEditor(column) {
    if (column.type === constants.CUSTOM_COLUMNS_TYPES.resources.id) {
      return this.#filterResourceOptions(column.options);
    }

    return column.options;
  }

  #filterResourceOptions(options) {
    const resources = this.#getOptionsForResourcesMultiselect();

    return options.reduce((result, option) => {
      const resource = resources.find(item => (item.id === option.resource_id) || (item.userId === option.userId));

      if (resource) {
        result.push({
          ...resource,
          id: option.id,
          sort_order: option.sort_order,
        });
      }

      return result;
    }, []);
  }

  #getOptionsForResourcesMultiselect() {
    const activeViewData = this.#instance.ganttDataApi.getActiveProjectData();
    let viewGanttIDs = activeViewData ? (activeViewData.ganttIDs || [activeViewData.gantt_id]) : [];
    let resources = [];
  
    for (const ganttID of viewGanttIDs) {
      const resourcesByGanttID = this.#instance.ganttDataApi.getResourcesByProjectIdForUI(ganttID);
  
      resources = _.unionBy(resources, resourcesByGanttID, 'id');
    }
  
    return resources;
  }
}

export default new UserColumns();
